import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { LogoMark } from "@/components/brand/Logo";
import {
  Sparkles, CheckCircle2, ClipboardList, ArrowRight, ArrowLeft,
  Target, MessageCircle, Users, HeartHandshake, Building2, School, Home,
} from "lucide-react";

const SERVICES = [
  "Leadership Development", "Communication Excellence", "Emotional Intelligence",
  "Conflict Resolution", "Team Collaboration & Accountability", "Customer Service Excellence",
  "Student Leadership Development", "Parent & Community Engagement",
  "Supervisor Development", "Strategic Planning Facilitation", "Custom Training",
];

const AUDIENCE_OPTIONS = [
  {
    key: "workplace",
    label: "Workplace",
    description: "Corporate teams, credit unions, hospitality, financial services, and any professional workplace.",
    examples: "Customers · staff · supervisors · leadership",
    icon: "Building2",
    color: "#5D3A1F",
  },
  {
    key: "school",
    label: "School",
    description: "Students, teachers, administrators, and parent-teacher communities.",
    examples: "Students · teachers · parents · school leadership",
    icon: "School",
    color: "#C28A2E",
  },
  {
    key: "community",
    label: "Community",
    description: "Youth groups, women's groups, church groups, non-profits, and community organizations.",
    examples: "Youth · women's groups · church groups · non-profits · residents",
    icon: "Home",
    color: "#8B5A2B",
  },
];

const emptyForm = {
  // Audience
  audience_type: "",
  // Contact
  full_name: "", organization_name: "", position: "", email: "", phone: "",
  preferred_contact: "Email",
  // Services
  services_interested: [],
  // Needs assessment (10 questions)
  industry: "",
  team_size: "",
  customer_interaction: "",
  previous_training: "",
  training_focus: "",
  current_challenge: "",
  communication_style: "",
  desired_outcome: "",
  success_vision_3mo: "",
  venue: "",
  additional_info: "",
};

const STEPS = [
  { key: "audience", title: "Who is this training for?", subtitle: "Pick the closest fit — we'll tailor the rest." },
  { key: "contact", title: "Let's start with hello", subtitle: "How can Joylance reach you?" },
  { key: "context", title: "Tell us about your context", subtitle: "Just enough context to serve you well." },
  { key: "challenge", title: "What are you working with?", subtitle: "The honest picture. No judgment." },
  { key: "vision", title: "What does success look like?", subtitle: "Paint the picture — we'll help you get there." },
  { key: "extras", title: "One more thing", subtitle: "Anything else Joylance should know?" },
];

const PublicDiscoveryPage = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => {
      const list = new Set(f.services_interested);
      if (list.has(s)) list.delete(s); else list.add(s);
      return { ...f, services_interested: Array.from(list) };
    });

  const canProceed = () => {
    if (step === 0) return !!form.audience_type;
    if (step === 1) return form.full_name && form.organization_name && form.email;
    return true;
  };

  const next = () => {
    setError("");
    if (!canProceed()) {
      setError("Please fill in the required fields to continue.");
      return;
    }
    if (step < STEPS.length - 1) setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep(Math.max(0, step - 1)); setError(""); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    setError("");
    if (!form.audience_type) {
      setError("Please select an audience.");
      setStep(0);
      return;
    }
    if (!form.full_name || !form.organization_name || !form.email) {
      setError("Name, organization, and email are required.");
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/needs-assessment", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF5] flex items-center justify-center p-6">
        <div className="max-w-xl text-center ccs-fade-up" data-testid="discovery-success">
          <div className="w-20 h-20 rounded-3xl bg-[#6B8E5A]/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#6B8E5A]" strokeWidth={1.6} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#C28A2E] font-bold">Assessment received</div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight text-[#2A1810] mt-4 leading-tight">
            Thank you, {form.full_name.split(" ")[0] || "friend"}
          </h1>
          <p className="text-[#5D3A1F] mt-5 text-base leading-relaxed">
            Your needs assessment is now with Joylance. She'll personally review it and craft
            a tailored proposal for <strong>{form.organization_name}</strong> — you'll hear from her within{" "}
            <strong>1 business day</strong>. Check your email for a confirmation.
          </p>
          <p className="text-sm text-[#6B5944] mt-8 italic">
            "Let everything you do be done in love." — 1 Corinthians 16:14
          </p>
          <Link to="/book" className="inline-block mt-10 text-sm text-[#C28A2E] font-semibold hover:underline" data-testid="discovery-home-link">
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  const currentStep = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="public-discovery-page">
      {/* Header */}
      <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/book" className="flex items-center gap-3" data-testid="discovery-home-link-header">
            <LogoMark size={40} />
            <div className="leading-tight">
              <div className="font-display text-base font-semibold text-[#2A1810]">The CCS Approach</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B5944] font-medium">Needs Assessment</div>
            </div>
          </Link>
          <div className="text-xs text-[#6B5944] hidden sm:block">Step {step + 1} of {STEPS.length}</div>
        </div>
        <div className="h-1 bg-[#EBE3D2]">
          <div
            className="h-1 bg-gradient-to-r from-[#5D3A1F] to-[#C28A2E] transition-all duration-500"
            style={{ width: `${progress}%` }}
            data-testid="discovery-progress"
          />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 lg:py-14">
        {/* Intro badge */}
        <div className="ccs-card ccs-grain p-6 lg:p-7 mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C28A2E]/15 text-[#5D3A1F] text-xs font-medium">
            <Sparkles className="w-3 h-3" /> {currentStep.subtitle}
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-3 leading-tight">
            {currentStep.title}
          </h1>
        </div>

        {/* STEP 0 — Audience selector */}
        {step === 0 && (
          <div className="ccs-card p-6 lg:p-7 space-y-3" data-testid="step-audience">
            <div className="text-sm text-[#6B5944] mb-2">
              Who this training will primarily serve. Community includes youth groups,
              women's groups, church groups, and other community organizations.
            </div>
            <div className="grid grid-cols-1 gap-3">
              {AUDIENCE_OPTIONS.map((a) => {
                const Icon = { Building2, School, Home }[a.icon];
                const selected = form.audience_type === a.key;
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => setF("audience_type", a.key)}
                    data-testid={`audience-${a.key}`}
                    className={`text-left p-5 rounded-2xl border-2 transition-all hover:-translate-y-0.5 ${
                      selected
                        ? "bg-white shadow-[0_12px_40px_rgba(45,24,16,0.15)]"
                        : "bg-white/60 border-[#EBE3D2] hover:bg-white"
                    }`}
                    style={selected ? { borderColor: a.color } : undefined}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform"
                        style={{
                          backgroundColor: `${a.color}${selected ? "20" : "12"}`,
                          transform: selected ? "scale(1.08)" : "scale(1)",
                        }}
                      >
                        <Icon className="w-6 h-6" style={{ color: a.color }} strokeWidth={1.6} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-display text-xl font-semibold text-[#2A1810]">{a.label}</h3>
                          {selected && <CheckCircle2 className="w-5 h-5 text-[#6B8E5A]" strokeWidth={2} />}
                        </div>
                        <p className="text-sm text-[#5D3A1F] mt-1.5 leading-relaxed">{a.description}</p>
                        <div className="text-[10px] uppercase tracking-[0.18em] font-bold mt-3" style={{ color: a.color }}>
                          {a.examples}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 1 — Contact */}
        {step === 1 && (
          <div className="ccs-card p-6 lg:p-7 space-y-4" data-testid="step-contact">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Full Name *</Label>
                <Input data-testid="d-name" value={form.full_name} onChange={(e) => setF("full_name", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Position / Title</Label>
                <Input data-testid="d-position" value={form.position} onChange={(e) => setF("position", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-sm font-medium">Organization / School / Community Name *</Label>
                <Input data-testid="d-org" value={form.organization_name} onChange={(e) => setF("organization_name", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Email *</Label>
                <Input data-testid="d-email" type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Phone / WhatsApp</Label>
                <Input data-testid="d-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-sm font-medium">Preferred contact method</Label>
                <Select value={form.preferred_contact} onValueChange={(v) => setF("preferred_contact", v)}>
                  <SelectTrigger data-testid="d-contact-method" className="h-11 rounded-lg bg-white border-[#EBE3D2]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Phone">Phone</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — Context */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-context">
            <div className="ccs-card p-6 lg:p-7 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">1</span> Sector / Context</Label>
                  <Input data-testid="d-industry" placeholder="e.g. Credit union, school, hospitality, community group, government" value={form.industry} onChange={(e) => setF("industry", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">2</span> Group Size</Label>
                  <Input data-testid="d-team-size" placeholder="e.g. 12 staff, 30 students, 50 community members" value={form.team_size} onChange={(e) => setF("team_size", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">3</span> Who will they interact with day-to-day?</Label>
                <Textarea rows={2} data-testid="d-customer-interaction" placeholder="Customers, students, parents, teammates, community members? In-person, phone, mixed?" value={form.customer_interaction} onChange={(e) => setF("customer_interaction", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">4</span> Previous Training</Label>
                <Textarea rows={2} data-testid="d-previous-training" placeholder="What training has this group had? When? Any frameworks they already use?" value={form.previous_training} onChange={(e) => setF("previous_training", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">5</span> Focus areas of interest (pick as many as apply)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-[#EBE3D2] rounded-xl bg-[#FDFBF5] max-h-48 overflow-y-auto">
                  {SERVICES.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`d-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <Checkbox checked={form.services_interested.includes(s)} onCheckedChange={() => toggleService(s)} />
                      <span className="text-[#2A1810]">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Challenge */}
        {step === 3 && (
          <div className="ccs-card p-6 lg:p-7 space-y-5" data-testid="step-challenge">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">6</span> Training Focus</Label>
              <Textarea rows={2} data-testid="d-training-focus" placeholder="Which CCS pillar or area do you want this to center on? Communication? Team collaboration? Customer service?" value={form.training_focus} onChange={(e) => setF("training_focus", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">7</span> Current Challenge</Label>
              <Textarea rows={4} data-testid="d-current-challenge" placeholder="What's the pain you're trying to solve right now? Be honest — the more specific, the more we can help." value={form.current_challenge} onChange={(e) => setF("current_challenge", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">8</span> Communication Style</Label>
              <Textarea rows={2} data-testid="d-communication-style" placeholder="How does your organization typically communicate? Formal vs informal? Hierarchical? Open feedback culture?" value={form.communication_style} onChange={(e) => setF("communication_style", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
            </div>
          </div>
        )}

        {/* STEP 4 — Vision */}
        {step === 4 && (
          <div className="ccs-card p-6 lg:p-7 space-y-5" data-testid="step-vision">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">9</span> Desired Outcome</Label>
              <Textarea rows={3} data-testid="d-desired-outcome" placeholder="What does success look like to you? What should feel, look, or sound different after the training?" value={form.desired_outcome} onChange={(e) => setF("desired_outcome", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium flex items-center gap-1.5"><span className="w-6 h-6 rounded-full bg-[#C28A2E] text-white text-xs font-bold flex items-center justify-center">10</span> 3-Month Success Vision</Label>
              <Textarea rows={4} data-testid="d-success-vision" placeholder="Three months after the workshop, what's concretely different? A specific behaviour, metric, or outcome you can point to?" value={form.success_vision_3mo} onChange={(e) => setF("success_vision_3mo", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium flex items-center gap-1.5"><Target className="w-4 h-4 text-[#C28A2E]" /> Venue</Label>
              <Input data-testid="d-venue" placeholder="On-site at your office? Off-site? Hybrid? Virtual?" value={form.venue} onChange={(e) => setF("venue", e.target.value)} className="h-11 rounded-lg bg-white border-[#EBE3D2]" />
            </div>
          </div>
        )}

        {/* STEP 5 — Extras + Review */}
        {step === 5 && (
          <div className="space-y-5" data-testid="step-extras">
            <div className="ccs-card p-6 lg:p-7 space-y-3">
              <Label className="text-sm font-medium">Anything else Joylance should know?</Label>
              <Textarea rows={5} data-testid="d-additional-info" placeholder="Budget parameters, timing constraints, key stakeholders, past experiences, faith-based considerations, anything at all…" value={form.additional_info} onChange={(e) => setF("additional_info", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
              <p className="text-xs text-[#6B5944] italic">This is optional but genuinely helpful — the more context, the more tailored the proposal.</p>
            </div>

            <div className="ccs-card ccs-grain p-6 lg:p-7 bg-[#5D3A1F]/5" data-testid="d-review">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold mb-3">Ready to send</div>
              <h3 className="font-display text-xl font-semibold text-[#2A1810]">Your assessment is ready</h3>
              <p className="text-sm text-[#5D3A1F] mt-2 leading-relaxed">
                Joylance will personally review your responses and craft a proposal tailored specifically for
                <strong> {form.organization_name || "your organization"}</strong> — using the exact context you've shared.
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full border border-[#EBE3D2]"><MessageCircle className="w-3 h-3 text-[#5D3A1F]" /> Communicate</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full border border-[#EBE3D2]"><Users className="w-3 h-3 text-[#C28A2E]" /> Collaborate</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full border border-[#EBE3D2]"><HeartHandshake className="w-3 h-3 text-[#8B5A2B]" /> Serve</span>
              </div>
            </div>
          </div>
        )}

        {error && <div className="text-sm text-[#A23E3E] bg-[#A23E3E]/10 rounded-lg p-3 mt-4" data-testid="discovery-error">{error}</div>}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-6">
          {step > 0 ? (
            <Button variant="outline" onClick={back} className="rounded-xl" data-testid="d-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : <div />}
          {step < STEPS.length - 1 ? (
            <Button onClick={next} disabled={!canProceed()} className="rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white ml-auto" data-testid="d-next-btn">
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white ml-auto h-12 px-6" data-testid="d-submit-btn">
              {submitting ? "Sending…" : (
                <>
                  <ClipboardList className="w-4 h-4 mr-2" /> Send Assessment to Joylance
                </>
              )}
            </Button>
          )}
        </div>

        <p className="text-xs text-[#6B5944] text-center italic mt-8">
          "Let everything you do be done in love." — 1 Corinthians 16:14
        </p>
      </main>
    </div>
  );
};

export default PublicDiscoveryPage;
